import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import accountIcon from "../../../assets/img/icons/how-to-resell-account.svg";
import bankIcon from "../../../assets/img/icons/how-to-resell-bank.svg";
import offersIcon from "../../../assets/img/icons/how-to-resell-offers.svg";
import saleIcon from "../../../assets/img/icons/how-to-resell-sale.svg";
import resellArrow from "../../../assets/img/icons/resell-cta-arrow.svg";
import resellBadge from "../../../assets/img/icons/resellable-rate-badge.svg";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import getLink, { LINK_TYPE } from "../../../config/Links";
import { selectUser } from "../../../redux/slices/userSlice";
import openAuthModal from "../../components/Auth/Auth";
import StaticPageBackground from "../Directsearch/StaticPageBackground";

const HERO_VIGNETTE_STYLE = {
  backgroundImage:
    "radial-gradient(75.6% 41% at 50% 50%, rgba(9, 17, 18, 0) 65.319%, #091112 100%)",
} as const;

const STEPS_OVERLAY_STYLE = {
  backgroundImage:
    "linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), radial-gradient(75.6% 50% at 50% 50%, #091112 50%, rgba(9, 17, 18, 0) 100%)",
} as const;

const RESELL_STEPS = [
  {
    icon: accountIcon,
    titleKey: "ds.how_to_resell_step1_title",
    bodyKey: "ds.how_to_resell_step1_body",
  },
  {
    icon: bankIcon,
    titleKey: "ds.how_to_resell_step2_title",
    bodyKey: "ds.how_to_resell_step2_body",
  },
  {
    icon: saleIcon,
    titleKey: "ds.how_to_resell_step3_title",
    bodyKey: "ds.how_to_resell_step3_body",
  },
  {
    icon: offersIcon,
    titleKey: "ds.how_to_resell_step4_title",
    bodyKey: "ds.how_to_resell_step4_body",
  },
] as const;

const SUPPORT_EMAIL = "support@takyon.io";

export default function HowToResell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  useEffect(() => {
    fireTagManagerEvent("landing_view", {
      user_id: user?._id ?? null,
      page_variant: "how_to_resell",
    });
  }, [user?._id]);

  const scrollToContent = () => {
    document.getElementById("how-to-resell-content")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const resellNow = () => {
    fireTagManagerEvent("click_resell_now", {
      logged_in: Boolean(user),
      source: "how_to_resell_hero",
    });

    if (!user) {
      openAuthModal();
      return;
    }

    navigate(getLink(LINK_TYPE.PROFILE_WALLET));
  };

  return (
    <main className="bg-[#091112]">
      <section
        className="relative isolate flex min-h-[620px] flex-col items-center justify-center overflow-hidden px-5 py-[140px] lg:min-h-[660px] lg:px-12 lg:py-[200px]"
        aria-labelledby="how-to-resell-title"
      >
        <StaticPageBackground variant="dark" />
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={HERO_VIGNETTE_STYLE}
        />

        <div className="relative z-[2] mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center gap-[30px] text-center">
          <div className="inline-flex h-9 items-center justify-center gap-2 rounded-[100px] border border-white/5 bg-white/10 py-[6px] pl-[6px] pr-3 shadow-[0_0_10px_rgba(0,0,0,0.05)] backdrop-blur-[50px]">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/40 bg-white">
              <img
                src={resellBadge}
                alt=""
                aria-hidden="true"
                className="h-4 w-4"
              />
            </span>
            <span className="[font-family:'Geist',_sans-serif] text-[16px] font-normal leading-5 text-white">
              {t("ds.how_to_resell_badge")}
            </span>
          </div>

          <div className="flex w-full max-w-[852px] flex-col items-center gap-4">
            <h1
              id="how-to-resell-title"
              className="m-0 w-full [font-family:'Geist',_sans-serif] text-[30px] font-medium leading-[32px] tracking-[-0.9px] text-white lg:text-[48px] lg:leading-[52px] lg:tracking-[-1.44px]"
            >
              {t("ds.how_to_resell_title")}
            </h1>
            <p className="m-0 w-full max-w-[540px] [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] tracking-[-0.32px] text-white/60 lg:text-[18px] lg:leading-6 lg:tracking-[-0.36px]">
              {t("ds.how_to_resell_subtitle")}
            </p>
          </div>

          <div className="flex flex-col items-center gap-2 sm:flex-row">
            <button
              type="button"
              className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-white/30 bg-[#091112] px-4 py-3 [font-family:'Geist',_sans-serif] text-[16px] font-medium leading-6 text-white transition-opacity hover:opacity-90"
              onClick={scrollToContent}
            >
              {t("ds.how_to_resell_cta_secondary")}
            </button>
            <button
              type="button"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-white/30 bg-[#9afee3] py-3 pl-4 pr-[14px] [font-family:'Geist',_sans-serif] text-[16px] font-medium leading-6 text-[#0a1213] shadow-[0_0_10px_rgba(154,255,228,0.45)] transition-opacity hover:opacity-90"
              onClick={resellNow}
            >
              <span>{t("ds.how_to_resell_cta_primary")}</span>
              <img
                src={resellArrow}
                alt=""
                aria-hidden="true"
                className="h-5 w-5"
              />
            </button>
          </div>
        </div>
      </section>

      <section
        id="how-to-resell-content"
        className="relative isolate scroll-mt-[75px] overflow-hidden px-5 py-[100px] lg:px-12 lg:py-[200px]"
        aria-labelledby="how-to-resell-steps-title"
      >
        <StaticPageBackground variant="dark" />
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={STEPS_OVERLAY_STYLE}
        />

        <div className="relative z-[2] mx-auto flex w-full max-w-[1200px] flex-col items-center gap-[60px]">
          <h2
            id="how-to-resell-steps-title"
            className="m-0 w-full max-w-[754px] [font-family:'Geist',_sans-serif] text-center text-[24px] font-medium leading-[30px] tracking-[-0.72px] text-white lg:text-[36px] lg:leading-[44px] lg:tracking-[-1.08px]"
          >
            {t("ds.how_to_resell_steps_title")}
          </h2>

          <div className="grid w-full grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {RESELL_STEPS.map((step) => (
              <article
                key={step.titleKey}
                className="flex min-h-[188px] flex-col items-start gap-4 rounded-[24px] border border-white/5 bg-white/[0.03] p-5 backdrop-blur-[25px] lg:min-h-[228px]"
              >
                <div
                  className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/10"
                  aria-hidden="true"
                >
                  <img src={step.icon} alt="" className="h-6 w-6" />
                </div>

                <div className="flex w-full flex-col items-start gap-2">
                  <h3 className="m-0 w-full [font-family:'Geist',_sans-serif] text-[20px] font-medium leading-6 tracking-[-0.6px] text-white">
                    {t(step.titleKey)}
                  </h3>
                  <p className="m-0 w-full [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] tracking-[-0.32px] text-white/60">
                    {t(step.bodyKey)}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <p className="m-0 w-full [font-family:'Geist',_sans-serif] text-center text-[16px] font-normal leading-[22px] tracking-[-0.32px] text-white/60 lg:text-[18px] lg:leading-6 lg:tracking-[-0.36px]">
            <span>{t("ds.how_to_resell_support_prefix")} </span>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-white underline decoration-solid [text-underline-position:from-font]"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}
